import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Chart, { ChartConfiguration, registerables } from 'chart.js/auto';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { LocalServiceService } from 'src/app/shareModule/services/local-service.service';
import { SharedService } from 'src/app/shareModule/services/shared.service';

Chart.register(...registerables);

interface MonthlyChartPoint {
    month: string;
    values: number[];
}

@Component({
    selector: 'app-map-custom-line-chart',
    templateUrl: './map-custom-line-chart.component.html',
    styleUrls: ['./map-custom-line-chart.component.scss']
})
export class MapCustomLineChartComponent implements OnChanges, OnDestroy {
    @Input() filter: string = '';
    @Input() isAdmin: boolean = false;

    public data: MonthlyChartPoint[] | null = null;
    public chartTitle = 'Monthly Activity Overview';
    public chartDescription = 'Dummy data only, replace with live metrics';
    public chartLabels: string[] = ['Series 1', 'Series 2'];

    @ViewChild('chartCanvas')
    set chartCanvasSetter(ref: ElementRef<HTMLCanvasElement> | null) {
        if (ref) {
            this.chartCanvas = ref;
            this.tryInitializeChartView();
        }
    }
    private chartCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('createDataModal') createDataModal!: TemplateRef<any>;

    private chart: Chart | null = null;
    private monthlyData: MonthlyChartPoint[] = [];
    private fullChartData: MonthlyChartPoint[] | null = null;
    public resolvedData: MonthlyChartPoint[] | null = null;
    private chartDataSub?: Subscription;
    private viewInitialized = false;
    private hasRequestedData = false;
    private flipTimeout: ReturnType<typeof setTimeout> | null = null;
    private autoFlipInterval: ReturnType<typeof setInterval> | null = null;
    private pendingFocusChartId: number | null = null;
    private formMode: 'create' | 'edit' = 'edit';

    editableTitle = '';
    editableDescription = '';
    editableLabels: string[] = [];
    editableFilteredDataRaw = '';
    editableDataRaw = '';
    isSaving = false;
    chartRecords: any[] = [];
    activeChartIndex = 0;
    activeChartId: number | null = null;
    flipDirection: 'next' | 'prev' = 'next';
    flipAnimating = false;
    hasNoData = false;
    private readonly colorPalette = [
        'rgba(99, 102, 241, 0.85)',
        '#f97316',
        '#0ea5e9',
        '#10b981',
        '#e11d48',
        '#14b8a6',
        '#a855f7',
    ];
    private modalRef: NgbModalRef | null = null;

    constructor(
        private modalService: NgbModal,
        private customerService: CustomerService,
        private sharedService: SharedService,
        private local: LocalServiceService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.sharedService.isBrowser()) {
            return;
        }

        if (changes['filter']) {
            this.hasRequestedData = false;
            this.chartRecords = [];
            this.activeChartIndex = 0;
            this.activeChartId = null;
            this.resolvedData = null;
            this.hasNoData = false;
            if (this.chart) {
                this.destroyChart();
            }
            this.loadChartData();
        }

        if (this.viewInitialized) {
            this.prepareData();
            this.refreshChart();
        } else {
            this.prepareData();
        }
    }

    ngOnDestroy(): void {
        this.chartDataSub?.unsubscribe();
        this.destroyChart();
        if (this.flipTimeout) {
            clearTimeout(this.flipTimeout);
            this.flipTimeout = null;
        }
        this.stopAutoFlip();
    }

    private prepareData(): void {
        if (this.data && this.data.length) {
            this.fullChartData = this.data.map((point) => this.normalizePoint(point));
            if (!this.resolvedData || !this.resolvedData.length) {
                this.resolvedData = this.fullChartData.slice(-6);
            }
        }

        let sourceData: MonthlyChartPoint[] | null = null;
        if (this.resolvedData && this.resolvedData.length) {
            sourceData = this.resolvedData.slice(-6);
        } else if (this.fullChartData && this.fullChartData.length) {
            sourceData = this.fullChartData.slice(-6);
        } else if (this.data && this.data.length) {
            sourceData = this.data.slice(-6);
        }

        if (sourceData && sourceData.length) {
            this.monthlyData = sourceData.map((point) => this.normalizePoint(point));
            this.hasNoData = false;
        } else {
            this.monthlyData = [];
            this.hasNoData = true;
        }
        this.ensureValuesMatchLabels();
    }

    private tryInitializeChartView(): void {
        if (this.viewInitialized || !this.chartCanvas || !this.sharedService.isBrowser()) {
            return;
        }

        this.viewInitialized = true;
        this.prepareData();

        if (this.monthlyData && this.monthlyData.length > 0) {
            this.renderChart();
        }

        if (!this.hasRequestedData) {
            this.loadChartData();
        } else if (this.chartRecords.length > 0) {
            const activeRecord = this.chartRecords[this.activeChartIndex];
            if (activeRecord) {
                this.applyChartRecord(activeRecord, false);
            }
        }

        this.initializeAutoFlip();
    }

    private renderChart(): void {
        if (!this.chartCanvas || !this.chartCanvas.nativeElement || this.chart) return;

        // Don't render chart if there's no data
        if (this.hasNoData || !this.monthlyData || this.monthlyData.length === 0) {
            return;
        }

        const { labels, datasets } = this.buildChartDataset();

        const config: ChartConfiguration = {
            type: 'line',
            data: {
                labels,
                datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'circle',
                            padding: 20,
                            color: '#475569',
                        },
                    },
                    tooltip: {
                        backgroundColor: '#0f172a',
                        padding: 12,
                        cornerRadius: 8,
                        titleColor: '#94a3b8',
                        bodyColor: '#e2e8f0',
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.y ?? context.parsed;
                                return `${label}: ${value}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#475569', font: { weight: 600 } },
                    },
                    y: {
                        grid: { color: 'rgba(226, 232, 240, 0.8)' },
                        ticks: { color: '#94a3b8' },
                        beginAtZero: true,
                    },
                },
            },
        };

        this.chart = new Chart(this.chartCanvas.nativeElement, config);
    }

    private buildChartDataset() {
        const labels = this.monthlyData.map((point) => point.month);

        const datasets = this.chartLabels.map((label, index) => {
            const color = this.colorPalette[index % this.colorPalette.length];
            return {
                type: 'line' as const,
                label: label || `Series ${index + 1}`,
                data: this.monthlyData.map((point) => Number(point.values[index] ?? 0)),
                borderColor: color,
                backgroundColor: this.getFillColor(color),
                borderWidth: 2,
                tension: 0.35,
                fill: false,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: color,
            };
        });

        return { labels, datasets };
    }

    private refreshChart(): void {
        if (!this.chartCanvas || !this.sharedService.isBrowser()) {
            return;
        }

        if (this.chart) {
            this.updateChart();
        } else {
            this.renderChart();
        }
    }

    openCreateDataModal(mode: 'create' | 'edit' = 'edit'): void {
        this.formMode = mode;

        if (mode === 'edit') {
            this.prepareData();
            this.editableTitle = this.chartTitle;
            this.editableDescription = this.chartDescription;
            this.editableLabels = this.chartLabels.length ? [...this.chartLabels] : ['Series 1'];
            if (this.editableLabels.length === 0) {
                this.editableLabels.push('Series 1');
            }
            const filteredRows = this.monthlyData.length
                ? this.monthlyData
                : this.getInitialFormRows().slice(-6);
            this.editableFilteredDataRaw = this.serializeDataRows(filteredRows, this.editableLabels.length);
            const initialData = this.getInitialFormRows();
            this.editableDataRaw = this.serializeDataRows(initialData, this.editableLabels.length);
        } else {
            this.editableTitle = '';
            this.editableDescription = '';
            this.editableLabels = this.chartLabels.length ? [...this.chartLabels] : ['Series 1'];
            if (!this.editableLabels.length) {
                this.editableLabels.push('Series 1');
            }
            const filteredRows = this.monthlyData.length
                ? this.monthlyData
                : [];
            this.editableFilteredDataRaw = this.serializeDataRows(filteredRows, this.editableLabels.length);
            this.editableDataRaw = '';
        }

        this.modalRef = this.modalService.open(this.createDataModal, {
            size: 'md',
            centered: true,
            backdrop: 'static',
        });

        this.modalRef.result.finally(() => (this.modalRef = null));
    }

    trackByIndex(index: number): number {
        return index;
    }

    closeCreateDataModal(): void {
        this.modalRef?.dismiss();
    }

    addMetaLabel(): void {
        this.editableLabels.push(`Series ${this.editableLabels.length + 1}`);
        const normalizedRows = this.parseEditableDataRows().map((row) => ({
            month: row.month,
            values: this.padValues(row.values, this.editableLabels.length),
        }));
        this.editableDataRaw = this.serializeDataRows(normalizedRows, this.editableLabels.length);
        const filteredRows = this.monthlyData.length
            ? this.monthlyData
            : normalizedRows.slice(-6);
        this.editableFilteredDataRaw = this.serializeDataRows(filteredRows, this.editableLabels.length);
    }

    removeMetaLabel(index: number): void {
        if (this.editableLabels.length <= 1) {
            this.editableLabels[0] = '';
            return;
        }
        this.editableLabels.splice(index, 1);
        const trimmedRows = this.parseEditableDataRows().map((row) => {
            const values = [...row.values];
            values.splice(index, 1);
            return { month: row.month, values };
        });
        this.editableDataRaw = this.serializeDataRows(trimmedRows, this.editableLabels.length);
        const filteredRows = this.monthlyData.length
            ? this.monthlyData.map((row) => ({
                month: row.month,
                values: this.padValues(row.values, this.editableLabels.length),
            }))
            : trimmedRows.slice(-6);
        this.editableFilteredDataRaw = this.serializeDataRows(filteredRows, this.editableLabels.length);
    }

    saveChartData(): void {
        if (this.isSaving) {
            return;
        }

        const rows = this.parseEditableDataRows();
        const sanitized = rows.map((row, idx) => ({
            month: row.month?.trim() || `Point ${idx + 1}`,
            values: this.padValues(row.values.map((value) => Number(value) || 0), this.editableLabels.length),
        }));

        if (!sanitized.length) {
            return;
        }

        if (!this.filter) {
            console.warn('Filter is required to save chart data.');
            return;
        }

        const sanitizedLabels = this.sanitizeEditableLabels();
        this.chartLabels = sanitizedLabels.length ? sanitizedLabels : [];
        this.editableLabels = [...this.chartLabels];

        const trimmedTitle = this.editableTitle.trim() || this.chartTitle;
        const trimmedDescription = this.editableDescription.trim() || this.chartDescription;

        const payload = {
            filter: this.filter,
            title: trimmedTitle,
            description: trimmedDescription,
            labels: JSON.stringify(this.chartLabels),
            data: JSON.stringify(sanitized),
            id: this.formMode === 'edit' ? this.activeChartId ?? undefined : undefined,
        };

        this.isSaving = true;

        this.customerService
            .addMapCustomLineChart(payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe({
                next: (response) => {
                    const displayData = sanitized.slice(-6);
                    this.fullChartData = sanitized;
                    this.resolvedData = displayData;
                    this.monthlyData = displayData;
                    this.hasNoData = false;
                    this.editableDataRaw = this.serializeDataRows(sanitized, this.chartLabels.length);
                    this.editableFilteredDataRaw = this.serializeDataRows(displayData, this.chartLabels.length);
                    this.chartTitle = trimmedTitle;
                    this.chartDescription = trimmedDescription;
                    this.refreshChart();
                    this.modalRef?.close();
                    const savedRecord = response?.body;
                    if (savedRecord?.id) {
                        this.pendingFocusChartId = savedRecord.id;
                    } else if (this.activeChartId) {
                        this.pendingFocusChartId = this.activeChartId;
                    }
                    this.loadChartData();
                },
                error: (error) => {
                    console.error('Failed to save chart data', error);
                },
            });
    }

    private updateChart(): void {
        if (!this.chart) return;
        const { labels, datasets } = this.buildChartDataset();
        this.chart.data.labels = labels;
        this.chart.data.datasets = datasets as any;
        this.chart.update();
    }

    switchChart(direction: 'next' | 'prev'): void {
        const total = this.chartRecords.length;
        if (!total || this.flipAnimating) {
            return;
        }

        let nextIndex: number;
        if (direction === 'next') {
            nextIndex = this.activeChartIndex + 1;
            if (nextIndex >= total) {
                // Reached the last record, stop auto-flip
                this.stopAutoFlip();
                return;
            }
        } else {
            nextIndex = (this.activeChartIndex - 1 + total) % total;
        }

        if (nextIndex === this.activeChartIndex) {
            return;
        }

        this.triggerFlipAnimation(direction);
        this.activeChartIndex = nextIndex;
        const nextRecord = this.chartRecords[this.activeChartIndex];
        this.applyChartRecord(nextRecord);
    }

    private triggerFlipAnimation(direction: 'next' | 'prev'): void {
        this.flipDirection = direction;
        this.flipAnimating = true;
        if (this.flipTimeout) {
            clearTimeout(this.flipTimeout);
        }
        this.flipTimeout = setTimeout(() => {
            this.flipAnimating = false;
        }, 650);
    }

    private initializeAutoFlip(): void {
        if (this.autoFlipInterval) {
            clearInterval(this.autoFlipInterval);
        }

        this.autoFlipInterval = setInterval(() => {
            if (this.chartRecords.length > 1 && !this.flipAnimating) {
                // Stop if we're already at the last record
                if (this.activeChartIndex >= this.chartRecords.length - 1) {
                    this.stopAutoFlip();
                    return;
                }
                this.switchChart('next');
            }
        }, 5000);
    }

    private stopAutoFlip(): void {
        if (this.autoFlipInterval) {
            clearInterval(this.autoFlipInterval);
            this.autoFlipInterval = null;
        }
    }

    private loadChartData(): void {
        const params: any = {};
        if (this.filter) {
            params.filter = this.filter;
        } else {
            this.chartRecords = [];
            this.activeChartIndex = 0;
            this.activeChartId = null;
            this.resolvedData = null;
            this.prepareData();
            this.refreshChart();
            return;
        }

        this.chartDataSub?.unsubscribe();
        this.hasRequestedData = true;

        this.chartDataSub = this.customerService
            .getMapCustomLineChart(params)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    const result = response?.body;
                    let filteredData = result;
                    if (Array.isArray(result)) {
                        filteredData = result.map((item: any) => {
                            const newItem = { ...item };
                            let parsedData = [];
                            if (typeof newItem.data === 'string') {
                                try {
                                    parsedData = JSON.parse(newItem.data);
                                } catch (e) {
                                    parsedData = [];
                                }
                            } else if (Array.isArray(newItem.data)) {
                                parsedData = newItem.data;
                            }
                            newItem.filteredData = Array.isArray(parsedData) ? parsedData.slice(-6) : [];
                            return newItem;
                        });
                    }
                    this.applyChartApiResponse(response?.body);
                },
                error: (error) => {
                    console.error('Failed to load chart data', error);
                    this.resolvedData = null;
                    this.prepareData();
                    this.refreshChart();
                },
            });
    }

    private applyChartApiResponse(response: any): void {
        const charts = Array.isArray(response) ? response : response ? [response] : [];
        this.chartRecords = charts;

        if (!this.chartRecords.length) {
            this.activeChartIndex = 0;
            this.activeChartId = null;
            this.resolvedData = null;
            this.hasNoData = true;
            this.monthlyData = [];
            this.chartLabels = [];
            this.chartTitle = '';
            this.chartDescription = '';
            // Ensure view is initialized if canvas exists
            if (this.chartCanvas && !this.viewInitialized) {
                this.viewInitialized = true;
            }
            // Don't render chart when no data
            if (this.chart) {
                this.destroyChart();
            }
            return;
        }

        if (this.pendingFocusChartId) {
            const focusIndex = this.chartRecords.findIndex((chart) => chart?.id === this.pendingFocusChartId);
            if (focusIndex >= 0) {
                this.activeChartIndex = focusIndex;
            }
            this.pendingFocusChartId = null;
        } else if (this.activeChartIndex >= this.chartRecords.length) {
            this.activeChartIndex = 0;
        }

        // Ensure view is initialized if canvas exists
        if (this.chartCanvas && !this.viewInitialized) {
            this.viewInitialized = true;
        }

        // Restart auto-flip if we have multiple records and we're not at the last one
        if (this.chartRecords.length > 1 && this.activeChartIndex < this.chartRecords.length - 1) {
            this.initializeAutoFlip();
        } else {
            this.stopAutoFlip();
        }

        const activeRecord = this.chartRecords[this.activeChartIndex];
        this.applyChartRecord(activeRecord, true);
    }

    private applyChartRecord(apiChart: any, forceRefresh = false): void {
        if (!apiChart) {
            this.resolvedData = null;
            this.fullChartData = null;
            this.activeChartId = null;
            this.prepareData();
            this.refreshChart();
            return;
        }

        this.activeChartId = apiChart?.id ?? null;

        const parsedLabels = this.parseJsonArray(apiChart?.labels, this.chartLabels);
        const parsedFiltered = this.parseJsonArray(apiChart?.filteredData ?? apiChart?.data, []);
        const parsedFull = this.parseJsonArray(apiChart?.data, parsedFiltered);

        this.resolvedData = Array.isArray(parsedFiltered)
            ? parsedFiltered.map((item) => this.normalizePoint(item))
            : null;
        this.fullChartData = Array.isArray(parsedFull)
            ? parsedFull.map((item) => this.normalizePoint(item))
            : null;

        if (parsedLabels.length) {
            this.chartLabels = parsedLabels.map((label: string, idx: number) => label || `Series ${idx + 1}`);
        }

        this.chartTitle = apiChart?.title || this.chartTitle;
        this.chartDescription = apiChart?.description || this.chartDescription;

        // Mark that we have data
        this.hasNoData = false;
        this.prepareData();

        if (forceRefresh && this.chart) {
            this.chart.destroy();
            this.chart = null;
        }

        // Use setTimeout to ensure DOM is ready and canvas is available
        setTimeout(() => {
            if (this.chartCanvas && this.chartCanvas.nativeElement) {
                this.refreshChart();
            } else if (!this.viewInitialized) {
                // Canvas not ready yet, will be initialized when canvas setter is called
                // The tryInitializeChartView will handle rendering
            }
        }, 0);
    }

    private parseJsonArray<T = any>(value: any, fallback: T[]): T[] {
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string' && value.trim()) {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : fallback;
            } catch {
                return fallback;
            }
        }
        return fallback;
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private getDefaultData(): MonthlyChartPoint[] {
        return [
            { month: 'Jan 2024', values: [32, 18] },
            { month: 'Feb 2024', values: [45, 22] },
            { month: 'Mar 2024', values: [51, 28] },
            { month: 'Apr 2024', values: [47, 24] },
            { month: 'May 2024', values: [58, 30] },
            { month: 'Jun 2024', values: [62, 34] },
            { month: 'Jul 2024', values: [68, 38] },
            { month: 'Aug 2024', values: [57, 32] },
            { month: 'Sep 2024', values: [49, 27] },
            { month: 'Oct 2024', values: [55, 31] },
            { month: 'Nov 2024', values: [61, 36] },
            { month: 'Dec 2024', values: [65, 40] },
        ];
    }

    private getFillColor(color: string): string {
        if (color.trim().startsWith('rgba')) {
            return color.replace(/rgba\(([^,]+),([^,]+),([^,]+),\s*([^)]+)\)/, 'rgba($1,$2,$3,0.2)');
        }
        return color;
    }

    private normalizePoint(point: any): MonthlyChartPoint {
        if (Array.isArray(point?.values)) {
            return {
                month: point.month ?? 'Jan 2024',
                values: [...point.values],
            };
        }

        const derivedValues: number[] = [];
        if (typeof point?.values === 'object' && point.values !== null) {
            Object.keys(point.values).forEach((key) => {
                derivedValues.push(Number(point.values[key]) || 0);
            });
        } else if ('visitors' in (point || {}) || 'replies' in (point || {})) {
            derivedValues.push(Number(point.visitors) || 0);
            derivedValues.push(Number(point.replies) || 0);
        } else {
            derivedValues.push(0, 0);
        }

        return {
            month: point?.month ?? 'Jan 2024',
            values: derivedValues,
        };
    }

    private ensureValuesMatchLabels(): void {
        // Don't set default labels if there's no data
        if (this.hasNoData) {
            return;
        }

        const maxSeries = Math.max(
            this.chartLabels.length,
            this.monthlyData.reduce((acc, point) => Math.max(acc, point.values?.length || 0), 0),
            1,
        );

        if (!this.chartLabels.length) {
            this.chartLabels = ['Series 1'];
        }

        while (this.chartLabels.length < maxSeries) {
            this.chartLabels.push(`Series ${this.chartLabels.length + 1}`);
        }

        this.monthlyData = this.monthlyData.map((point) => ({
            month: point.month,
            values: this.padValues(point.values || [], this.chartLabels.length),
        }));
    }

    private padValues(values: number[], targetLength?: number): number[] {
        const desiredLength = targetLength ?? (this.editableLabels.length || this.chartLabels.length || 1);
        const padded = [...values];
        while (padded.length < desiredLength) {
            padded.push(0);
        }
        if (padded.length > desiredLength) {
            padded.length = desiredLength;
        }
        return padded;
    }

    private sanitizeEditableLabels(): string[] {
        const sanitized = this.editableLabels
            .map((tag) => tag?.trim())
            .filter((tag, idx, arr) => !!tag && arr.indexOf(tag) === idx);

        return sanitized.length ? sanitized : ['Series 1'];
    }

    private getInitialFormRows(): MonthlyChartPoint[] {
        const baseData =
            (this.fullChartData && this.fullChartData.length
                ? this.fullChartData
                : this.monthlyData && this.monthlyData.length
                    ? this.monthlyData
                    : null) ||
            [];

        const rows =
            baseData.length > 0
                ? baseData
                : [
                    {
                        month: 'Jan-2024',
                        values: this.editableLabels.map(() => 0),
                    },
                ];

        return rows.map((point) => ({
            month: point.month,
            values: this.padValues([...point.values], this.editableLabels.length),
        }));
    }

    private parseEditableDataRows(): MonthlyChartPoint[] {
        if (!this.editableDataRaw?.trim()) {
            return [];
        }

        return this.editableDataRaw
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => !!line)
            .map((line, idx) => {
                const parts = line.split(/\s+/);
                const month = parts.shift() ?? `Point ${idx + 1}`;
                const values = parts.map((value) => Number(value) || 0);
                return { month, values };
            });
    }

    private serializeDataRows(rows: MonthlyChartPoint[], labelCount?: number): string {
        if (!rows.length) {
            return '';
        }

        return rows
            .map((row) => {
                const paddedValues = this.padValues(row.values || [], labelCount);
                return [row.month, ...paddedValues].join(' ');
            })
            .join('\n');
    }

    deleteChart(): void {
        if (!this.activeChartId) {
            return;
        }

        this.local.alertConfirm({
            message: 'Are you sure you want to delete this chart?'
        }).then((resp) => {
            if (resp.isConfirmed) {
                this.customerService.deleteMapCustomLineChart(this.activeChartId).subscribe({
                    next: (response) => {
                        this.loadChartData();
                    }
                });
            }
        });
    }
}
